import {
  mkHeaders,
  PaginatedResponse,
  UnauthorisedError,
  ApiError,
} from "./utils"

export type Role = "ORG_ADMIN" | "USER"

export interface User {
  email: string
  id: string
  name: string
  role: Role
}

// TODO deal with pagination
export const listAllUsers = async (
  baseUrl: string,
  token: string,
): Promise<User[]> => {
  const res = await fetch(`${baseUrl}/api/users`, {
    headers: mkHeaders(token),
    credentials: "include",
  })

  if (res.status === 401) {
    throw new UnauthorisedError(
      `Unauthorised access - operation=listAllUsers statusCode=${res.status}`,
    )
  }

  if (res.status !== 200) {
    const msg = await res.text()
    throw new ApiError(
      `Failed to list users - statusCode=${res.status} message=${msg}`,
      res.status,
      "listAllUsers",
    )
  }

  const page: PaginatedResponse<User> = await res.json()
  return page.content
}

export type PartialUser = Omit<User, "id">

export const createUser = async (
  partial: PartialUser,
  baseUrl: string,
  token: string,
): Promise<User> => {
  const res = await fetch(`${baseUrl}/api/users`, {
    method: "POST",
    headers: mkHeaders(token, "POST"),
    credentials: "include",
    body: JSON.stringify(partial),
  })

  if (res.status === 401) {
    throw new UnauthorisedError(
      `Unauthorised access - operation=createUser statusCode=${res.status} email=${partial.email}`,
    )
  }

  if (res.status === 403) {
    throw new ApiError(
      `CSRF token validation failed - operation=createUser statusCode=${res.status} email=${partial.email}`,
      res.status,
      "createUser",
      { email: partial.email },
    )
  }

  if (res.status !== 201) {
    const msg = await res.text()
    throw new ApiError(
      `Failed to create user - statusCode=${res.status} email=${partial.email} message=${msg}`,
      res.status,
      "createUser",
      { email: partial.email },
    )
  }

  const user: User = await res.json()
  return user
}

export const deleteUser = async (
  userId: User["id"],
  baseUrl: string,
  token: string,
): Promise<void> => {
  const res = await fetch(`${baseUrl}/api/users/${userId}`, {
    method: "DELETE",
    headers: mkHeaders(token, "DELETE"),
    credentials: "include",
  })

  if (res.status === 401) {
    throw new UnauthorisedError(
      `Unauthorised access - operation=deleteUser statusCode=${res.status} userId=${userId}`,
    )
  }

  if (res.status === 403) {
    throw new ApiError(
      `CSRF token validation failed - operation=deleteUser statusCode=${res.status} userId=${userId}`,
      res.status,
      "deleteUser",
      { userId },
    )
  }

  if (res.status !== 200) {
    const msg = await res.text()
    throw new ApiError(
      `Failed to delete user - statusCode=${res.status} userId=${userId} message=${msg}`,
      res.status,
      "deleteUser",
      { userId },
    )
  }
}
